import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { STATE_STYLES, supplementsForToday } from "../lib/planner";
import { Download, Check, Loader2, Leaf } from "lucide-react";
import { toast } from "sonner";

const WIN_AFF = {
  Rest: "You honored your need for rest today. That's wisdom, not weakness.",
  Gentle: "You moved gently, and that was exactly enough.",
  Steady: "Steady hands, steady heart — you showed up for yourself.",
  Push: "You rode the bright wave and made it count.",
};

const Section = ({ title, items, emptyText }) => (
  <div className="text-left">
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-400 mb-1.5">{title}</p>
    {items.length ? (
      <ul className="space-y-1">
        {items.map((label, i) => (
          <li key={`${title}-${label}-${i}`} className="flex items-center gap-2 text-sm text-stone-700 dark:text-stone-200">
            <Check className="h-3.5 w-3.5 text-[#b1543b] shrink-0" />
            <span className="truncate">{label}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-stone-400">{emptyText}</p>
    )}
  </div>
);

export const WinsRecap = ({ open, onOpenChange, entry, tasks, habitLog = [], settings, progress, date }) => {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  if (!entry) return null;

  const s = STATE_STYLES[entry.label] || STATE_STYLES.Steady;
  const ringPct = Math.round((progress?.frac || 0) * 100);
  const deg = Math.round((progress?.frac || 0) * 360);

  const completedTasks = [
    ...(tasks?.top3 || []),
    ...(tasks?.fits_today || []),
    ...(tasks?.maybe_skip || []),
  ].filter((t) => t.completed);
  const habits = tasks?.habits || [];
  const habitsDone = habits.filter((h) => habitLog.includes(h.id));
  const supToday = supplementsForToday(settings?.supplements || []);
  const suppTaken = (entry.supplements_taken || []).filter((n) => supToday.some((x) => x.name === n));

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true,
        backgroundColor: "#e7d2c4",
      });
      const link = document.createElement("a");
      link.download = `ebbly-wins-${date}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved your wins card 🌿");
    } catch (err) {
      console.error("Wins card export failed:", err);
      toast.error("Couldn't save the image — try again");
    }
    setDownloading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wins-recap-dialog" className="rounded-3xl max-w-sm p-0 overflow-hidden bg-transparent border-0 shadow-none">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading text-2xl text-[#3b2a24] dark:text-stone-100">Today's wins</DialogTitle>
          <DialogDescription className="text-sm text-stone-400">
            A gentle recap of everything you tended to today.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6">
          <div
            ref={cardRef}
            data-testid="wins-card"
            className="rounded-3xl bg-[#e7d2c4] border border-[#d6bfae] p-7 text-center"
          >
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Leaf className="h-4 w-4 text-[#b1543b]" />
              <span className="font-heading text-lg font-bold text-[#b1543b] tracking-tight">Ebbly</span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-5">
              {entry.phase} phase · Cycle day {entry.cycle_day}
            </p>

            <div
              className="mx-auto h-28 w-28 rounded-full p-[8px]"
              style={{ background: `conic-gradient(${s.hex} ${deg}deg, rgba(120,120,120,0.15) ${deg}deg)` }}
            >
              <div className="h-full w-full rounded-full bg-white flex flex-col items-center justify-center">
                <span className="font-heading text-3xl font-bold" style={{ color: s.hex }} data-testid="wins-ring-pct">
                  {ringPct}%
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">ring full</span>
              </div>
            </div>

            <p className="mt-5 font-heading italic text-lg text-stone-700 leading-snug" data-testid="wins-affirmation">
              "{WIN_AFF[entry.label] || WIN_AFF.Steady}"
            </p>

            <div className="mt-6 space-y-4 border-t border-[#d6bfae] pt-5">
              <Section
                title={`Tasks done (${completedTasks.length})`}
                items={completedTasks.map((t) => t.content)}
                emptyText="No tasks today — and that's okay 🌱"
              />
              <Section
                title={`Habits (${habitsDone.length}/${habits.length})`}
                items={habitsDone.map((h) => h.content)}
                emptyText="No habits ticked yet"
              />
              <Section
                title={`Supplements (${suppTaken.length}/${supToday.length})`}
                items={suppTaken}
                emptyText="None taken today"
              />
            </div>

            <p className="mt-6 text-[11px] text-stone-400">Small and kind is enough. 🌿</p>
          </div>

          <Button
            data-testid="download-wins-btn"
            onClick={download}
            disabled={downloading}
            className="mt-4 w-full h-12 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-white font-semibold gap-2"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Save as image
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
